// Phrases played since Test me was switched on.
//
// Test me hides the count for N phrases and shows it for N (core's `hiddenAfter`). It counts phrases *played*, not the position in
// the song, so it runs the same inside a loop, where the position goes round: the numeral is shown for N passes of a loop of one
// 8-count and hidden for N.
//
// The count is kept by looking at the heard position once per frame. Within one clock (clock.ts) a count 1 is crossed when the
// position moves forward past it, or when the loop wraps (the loop starts on a count 1, so a wrap crosses one). A new clock (play,
// pause, seek, speed or loop change) starts a new baseline and counts nothing by itself, so a seek or a change of speed does not move
// the pattern. Laps that went by between two frames (a hidden tab) are counted from the clock's own lap count.

import { phrasesBetween, type CountMap } from '@salsacount/core';
import { clockAt, lapsAt, type Clock } from './clock.js';

/** Seconds before a loop's end that still count as inside its last phrase (the end is a count 1 and its float time is not exact). */
const EDGE_S = 1e-6;

interface Seen {
  readonly clock: Clock;
  readonly laps: number;
  readonly song: number;
}

export class PlayedPhrases {
  private played = 0;
  private seen: Seen | null = null;

  /** Phrases played so far. */
  get count(): number {
    return this.played;
  }

  /** Test me was switched on (or the song changed): this phrase is the first, and nothing has been played yet. */
  reset(): void {
    this.played = 0;
    this.seen = null;
  }

  /**
   * Take a reading. `clock` is the transport's clock, `nodeTime` the context time of the sound the listener hears now (context time
   * minus the output latency), `song` the heard position. Returns the phrases played since `reset`.
   */
  update(map: CountMap, clock: Clock, nodeTime: number, song: number): number {
    const c = clockAt(clock, nodeTime);
    const laps = lapsAt(c, nodeTime);
    const last = this.seen;
    this.seen = { clock: c, laps, song };
    if (!last || last.clock !== c || !c.playing) return this.played; // a new baseline: a seek, a change of speed or loop, a pause
    if (laps === last.laps) {
      if (song > last.song) this.played += phrasesBetween(map, last.song, song);
      return this.played;
    }
    const loop = c.loop;
    if (!loop) return this.played;
    // The loop wrapped `laps - last.laps` times: the rest of the phrase run the first time, a whole loop each further time, the run into the new lap.
    const edge = loop.end - EDGE_S;
    const perLap = phrasesBetween(map, loop.start, edge) + 1;
    this.played += phrasesBetween(map, last.song, edge) + 1 + (laps - last.laps - 1) * perLap + phrasesBetween(map, loop.start, song);
    return this.played;
  }
}
